/**
 * Clerk isolation module – the SINGLE file that imports from @clerk/clerk-react.
 * Every other module that needs Clerk must import from here.
 *
 * Re-exports Clerk React components/hooks for Preact via preact/compat aliasing.
 * Also re-exports shared helpers (getAuthToken for old callers) for backward compat.
 */
export {
  // Provider
  ClerkProvider,
  // Authentication components
  SignIn,
  SignUp,
  SignInButton,
  SignUpButton,
  SignOutButton,
  // Conditional rendering
  SignedIn,
  SignedOut,
  // Loading states
  ClerkLoading,
  ClerkLoaded,
  // Redirects
  RedirectToSignIn,
  RedirectToSignUp,
  // User components
  UserButton,
  UserProfile,
  // Organization components
  OrganizationSwitcher,
  OrganizationProfile,
  CreateOrganization,
  // Hooks
  useAuth,
  useUser,
  useClerk,
  useSession,
  useSessionList,
  useSignIn,
  useSignUp,
  useOrganization,
  useOrganizationList,
} from "@clerk/clerk-react";

// ---------------------------------------------------------------------------
// Environment helpers
// ---------------------------------------------------------------------------

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

type ClerkLike = {
  session?: {
    getToken: () => Promise<string | null>;
  } | null;
};

declare global {
  interface Window {
    Clerk?: ClerkLike;
  }
}

export function getClerkPublishableKey(): string {
  return typeof CLERK_PUBLISHABLE_KEY === "string" ? CLERK_PUBLISHABLE_KEY : "";
}

export function isClerkConfigured(publishableKey = getClerkPublishableKey()): boolean {
  return publishableKey.startsWith("pk_");
}

/**
 * Legacy / backward-compat helper: gets a Clerk session token via the
 * global `window.Clerk` object. New code should prefer `useAuth().getToken()`.
 */
export async function getClerkAuthToken(): Promise<string | null> {
  if (typeof window === "undefined") {
    return null;
  }
  return window.Clerk?.session?.getToken() ?? null;
}
